import { useCallback, useEffect, useState } from 'react';
import { Site } from '../../types/Site';
import { logError } from '../../utils/logger';
import { ForNeighborsError, classifyForNeighborsError } from './classifyForNeighborsError';

export interface ForNeighborsSitesResponse {
  sites: Site[];
  resultsTruncated: boolean;
}

export interface ForNeighborsSitesFetchResult {
  sites: Site[];
  loading: boolean;
  error: ForNeighborsError | null;
  resultsTruncated: boolean;
  refetch: () => void;
}

// Shared fetch-effect body for every hook that fetches a Site[] from a ForNeighbours RPC
// operation (useSitesWithinRadius's GetSitesWithinRadius, useSiteSearch's SearchAllSites): same
// cancellation-guarded fetch/classify/clear-or-preserve-on-inactive shape, differing only in what
// "inactive" means to each caller and what triggers a re-fetch (the caller's own `deps`).
//
// `active=false` means "don't fetch right now". `preserveWhenInactive` decides what happens to
// the current sites/resultsTruncated while inactive: false clears them (nothing valid to show --
// e.g. useSiteSearch's empty-query idle state), true leaves them alone (still valid, just not
// being shown right now -- e.g. useSitesWithinRadius paused while a search is active elsewhere).
// `deps` is spread directly into a useEffect dependency array (below), so -- as with any React
// hook dependency list -- it must keep a stable length across renders; a caller whose deps array
// changes length between renders hits React's changed-size-of-dependency-array error.
export function useForNeighborsSitesFetch(
  active: boolean,
  preserveWhenInactive: boolean,
  fetchSites: () => Promise<ForNeighborsSitesResponse>,
  deps: ReadonlyArray<unknown>,
  onSuccess?: (response: ForNeighborsSitesResponse) => void
): ForNeighborsSitesFetchResult {
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(active);
  const [error, setError] = useState<ForNeighborsError | null>(null);
  const [resultsTruncated, setResultsTruncated] = useState(false);
  const [refetchToken, setRefetchToken] = useState(0);

  // Reset loading/error synchronously when the fetch identity changes (React's "adjust state
  // during render" pattern) -- otherwise the render that flips to a new fetch identity still
  // carries the *previous* cycle's loading/error for one paint, until the effect below runs.
  // That stale state can paint something actively wrong: e.g. entering search mode with a fresh,
  // not-yet-fetched query briefly renders as "no results" (loading stuck false, sites still [])
  // instead of the spinner.
  const depsKey = JSON.stringify([active, preserveWhenInactive, ...deps, refetchToken]);
  const [lastDepsKey, setLastDepsKey] = useState(depsKey);
  if (depsKey !== lastDepsKey) {
    setLastDepsKey(depsKey);
    setLoading(active);
    setError(null);
  }

  useEffect(() => {
    if (!active) {
      setLoading(false);
      if (!preserveWhenInactive) {
        // Functional updaters bail out of the re-render when there's nothing to clear.
        setSites((prev) => (prev.length === 0 ? prev : []));
        setResultsTruncated((prev) => (prev ? false : prev));
      }
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const response = await fetchSites();
        if (cancelled) return;
        setSites(response.sites ?? []);
        setResultsTruncated(response.resultsTruncated ?? false);
        onSuccess?.(response);
      } catch (e) {
        if (cancelled) return;
        logError(e as Error);
        setError(classifyForNeighborsError(e));
        setSites([]);
        setResultsTruncated(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps is the caller's own dependency list; fetchSites/onSuccess are intentionally excluded (see module comment)
  }, [active, preserveWhenInactive, ...deps, refetchToken]);

  const refetch = useCallback(() => setRefetchToken((t) => t + 1), []);

  return { sites, loading, error, resultsTruncated, refetch };
}
